<template>
    <v-container>
        <h2 class="mb-4">{{ mode === 'create' ? 'Create Page' : mode === 'edit' ? 'Edit Page' : 'View Page' }}</h2>
        <div v-if="mode && !['edit', 'create'].includes(mode)" class="edit-button-wrapper">
            <v-btn class="mx-2" dark medium color="primary" :loading="navigating" :disabled="navigating || submitting" @click="onEdit">
                <v-icon dark>mdi-account-edit</v-icon>
            </v-btn>
        </div>
        <v-form :readonly="readOnly">
            <v-text-field
                v-model="page.title"
                :rules="rules"
                label="Title"
                :clearable="clearable"
            />
            <v-select
                v-model="page.sport"
                :items="sports"
                item-text="title"
                item-value="id"
                return-object
                label="Sport"
                :clearable="clearable"
            />
            <div class="body-editor">
                <span class="body-label">Description</span>
                <editor
                    ref="editor"
                    v-model="page.description"
                    :api-key="tinymceApiKey"
                    :init="editorInit"
                />
            </div>
            <v-file-input
                v-if="mode !== 'show'"
                v-model="newGalleryImages"
                label="Page Gallery Images"
                multiple
                chips
                small-chips
                filled
                show-size
                prepend-icon="mdi-image-multiple"
                @change="createGalleryPreviews"
            />
            <div v-if="visibleGalleryItems.length" class="gallery-wrapper">
                <div v-for="item in visibleGalleryItems" :key="item.uid" class="gallery-item" :class="{ 'gallery-item-new': !item.id }">
                    <div v-if="mode !== 'show'" class="gallery-delete-btn">
                        <v-btn class="mx-1 white--text" x-small color="secondary" :loading="deletingImage" :disabled="deletingImage || submitting || navigating" @click="onGalleryImageDelete(item)">
                            X
                        </v-btn>
                    </div>
                    <v-img v-if="item.url" class="gallery-img-preview" :src="item.url" contain height="120" />
                </div>
            </div>
        </v-form>
        <div v-if="mode !== 'show'" class="submit-button-wrapper">
            <v-btn class="mx-2 white--text" large color="primary" @click="onSubmit" :loading="submitting" :disabled="submitting || navigating || shouldDisableSubmit()">
                Submit
            </v-btn>
        </div>
    </v-container>
</template>

<script>
    import pageService from '@/services/page-service'
    import sportService from '@/services/sport-service'
    import Editor from '@tinymce/tinymce-vue'

    export default {
        name: 'PageForm',
        components: { Editor },
        data: () => {
            return {
                mode: null,
                readOnly: false,
                clearable: true,
                submitting: false,
                navigating: false,
                deletingImage: false,
                tinymceApiKey: process.env.VUE_APP_TINYMCE_API_KEY,
                page: {
                    title: '',
                    sport: null,
                    description: null
                },
                newGalleryImages: [],
                existingGalleryImages: [],
                galleryPreviewImages: [],
                pageId: null,
                sports: [],
                rules: [
                    v => !!v || 'Title is required'
                ],
                editorInit: {
                    selector: 'page_content',
                    menubar: false,
                    plugins: [
                        'advlist autolink lists charmap print preview anchor',
                        'searchreplace visualblocks fullscreen',
                        'insertdatetime table paste help wordcount link',
                        'image media'
                    ],
                    automatic_uploads: true,
                    powerpaste_allow_local_images: true,
                    file_picker_types: 'image',
                    resize: 'both',
                    min_height: 600,
                    fontsize_formats: '8px 10px 12px 14px 18px 24px 36px',
                    toolbar: 'undo redo | styleselect | forecolor | bold italic | fontselect fontsizeselect | emoticons | alignleft aligncenter alignright alignjustify | numlist bullist | outdent indent | link unlink | image media',
                    statusbar: true
                },
            }
        },
        computed: {
            visibleGalleryItems() {
                return [...this.existingGalleryImages, ...this.galleryPreviewImages].filter(item => !item.shouldDelete)
            }
        },
        created() {
            this.mode = this.$route.params.mode
            this.loadSports()
            if (this.mode === 'create') {
                return
            } else if (this.mode === 'show') {
                this.readOnly = true
                this.clearable = false
                this.editorInit.setup = function(editor) {
                    editor.setMode('readonly')
                }
            }

            this.pageId = this.$route.params.pageId
            if (this.pageId) {
                pageService.getPageById(this.pageId).then(resp => {
                    this.page = resp.data.page || resp.data.data || {}
                    this.page.sport = this.page.sport || null
                    this.existingGalleryImages = (this.page.imageGallery || []).map(image => ({
                        ...image,
                        url: process.env.VUE_APP_GOOGLE_BUCKET_URL + image.imageName,
                        shouldDelete: false,
                        uid: `existing-${image.id}`
                    }))
                })
            }
        },
        methods: {
            loadSports() {
                sportService.getAllSports().then(resp => {
                    this.sports = resp.data.data || []
                })
            },
            shouldDisableSubmit() {
                return !this.page.title
            },
            async onEdit() {
                this.navigating = true
                try {
                    await this.$router.push(`/pages/${this.pageId}/edit`)
                } finally {
                    this.navigating = false
                }
            },
            async createPage() {
                const response = await pageService.createPage(this.buildPageFormData())
                if (response.data.id) {
                    this.pageId = response.data.id
                }
                this.$router.push('/pages')
            },
            async editPage() {
                await pageService.editPageById(this.pageId, this.buildPageFormData())
                this.$router.push('/pages')
            },
            buildPageFormData() {
                const formData = new FormData()
                formData.append('title', this.page.title || '')
                formData.append('description', this.page.description || '')
                formData.append('sportId', this.page.sport ? this.page.sport.id : '')

                this.galleryPreviewImages.forEach(image => {
                    if (image.file && !image.shouldDelete) {
                        formData.append('galleryFiles[]', image.file)
                    }
                })

                const galleryDeleteIds = this.existingGalleryImages
                    .filter(image => image.id && image.shouldDelete)
                    .map(image => image.id)

                if (galleryDeleteIds.length) {
                    formData.append('galleryDeleteIds', JSON.stringify(galleryDeleteIds))
                }

                return formData
            },
            createGalleryPreviews() {
                const files = Array.isArray(this.newGalleryImages) ? this.newGalleryImages : []
                this.galleryPreviewImages = files.filter(file => this.hasSelectedImage(file)).map((file, index) => ({
                    uid: `new-${file.name}-${file.size}-${index}`,
                    url: URL.createObjectURL(file),
                    file,
                    shouldDelete: false
                }))
            },
            onGalleryImageDelete(item) {
                this.deletingImage = true
                if (item.id) {
                    item.shouldDelete = true
                    this.existingGalleryImages = [...this.existingGalleryImages]
                } else {
                    const remaining = this.galleryPreviewImages.filter(image => image.uid !== item.uid)
                    this.galleryPreviewImages = remaining
                    this.newGalleryImages = remaining.map(image => image.file)
                }
                this.deletingImage = false
            },
            hasSelectedImage(image) {
                return image && typeof image === 'object' && image.size !== undefined && image.name
            },
            async onSubmit() {
                this.submitting = true
                try {
                    if (this.mode === 'edit') {
                        await this.editPage()
                    } else if (this.mode === 'create') {
                        await this.createPage()
                    }
                } finally {
                    this.submitting = false
                }
            }
        }

    }
</script>

<style>
.tox .tox-editor-header {
    z-index: 0 !important;
}
.tox .tox-statusbar {
    display: none !important;
}

.tox .tox-toolbar--scrolling {
    flex-wrap: wrap !important;
}
</style>

<style scoped>
.body-label {
    color: rgba(0, 0, 0, 0.6);
}

.body-editor {
    margin-bottom: 2vh;
}

.edit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.submit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 2vh;
}

.gallery-wrapper {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 12px;
    margin-top: 8px;
    padding: 10px;
    border: 1px solid rgba(33, 48, 80, 0.16);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.75);
    margin-top: 12px;
}

.gallery-item {
    position: relative;
    border: 2px solid #d3d3d3;
    border-radius: 10px;
    margin-bottom: 2px;
    padding: 8px;
}

.gallery-item-new {
    border-color: #4caf50;
}

.gallery-delete-btn {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 1;
}

.gallery-img-preview {
    width: 100%;
    height: 120px;
}
</style>

